import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Trash2, Eye, Package, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

interface PurchaseInvoice {
  id: string;
  purchaseNumber: string;
  supplierId?: string;
  supplierName?: string;
  purchaseDate: string | Date;
  status: 'draft' | 'received' | 'voided';
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  reference?: string;
  notes?: string;
  receivedAt?: string | Date;
  createdAt: string | Date;
  updatedAt: string | Date;
}

interface PurchaseLineItem {
  id: string;
  invoiceId: string;
  productId: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  createdAt: string | Date;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  price: number;
  cost?: number;
  category: string;
  taxRate: number;
  stockQuantity: number;
  isActive: boolean;
  createdAt: string | Date;
  updatedAt: string | Date;
}

const PurchasesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState<PurchaseInvoice | null>(null);
  const [viewingLineItems, setViewingLineItems] = useState<PurchaseLineItem[]>([]);

  const [formData, setFormData] = useState({
    supplierName: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    reference: '',
    notes: '',
    taxRate: '0',
  });

  const [lineItems, setLineItems] = useState<Array<{ productId: string; quantity: string; unitCost: string }>>([
    { productId: '', quantity: '1', unitCost: '0' },
  ]);

  const { data: invoicesData, isLoading: invoicesLoading, isError: invoicesError } = useQuery<{ invoices: PurchaseInvoice[] }>({
    queryKey: ['purchases', 'invoices'],
    queryFn: async () => {
      const res = await api.get('/api/purchases');
      return { invoices: res.data?.data?.invoices || res.data?.invoices || [] };
    },
  });

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await api.get('/api/products');
      return (res.data?.data?.products || res.data?.products || []) as Product[];
    },
  });

  const invoices = invoicesData?.invoices || [];

  const filteredInvoices = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((inv) => {
      return (
        (inv.purchaseNumber || '').toLowerCase().includes(q) ||
        (inv.supplierName || '').toLowerCase().includes(q) ||
        (inv.reference || '').toLowerCase().includes(q)
      );
    });
  }, [invoices, searchTerm]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MW', {
      style: 'currency',
      currency: 'MWK',
    }).format(amount);
  };

  const formatDate = (dateValue: string | Date) => {
    return new Date(dateValue).toLocaleDateString('en-MW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const openCreateModal = () => {
    setFormData({
      supplierName: '',
      purchaseDate: new Date().toISOString().split('T')[0],
      reference: '',
      notes: '',
      taxRate: '0',
    });
    setLineItems([{ productId: '', quantity: '1', unitCost: '0' }]);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const viewInvoiceMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      const res = await api.get(`/api/purchases/invoices/${invoiceId}`);
      return res.data as { invoice: PurchaseInvoice; lineItems: PurchaseLineItem[] };
    },
    onSuccess: (data) => {
      setViewingInvoice(data.invoice);
      setViewingLineItems(data.lineItems || []);
      setIsViewModalOpen(true);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to load purchase invoice');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      await api.delete(`/api/purchases/invoices/${invoiceId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases', 'invoices'] });
      toast.success('Purchase invoice deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete purchase invoice');
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        supplierName: formData.supplierName.trim() || undefined,
        purchaseDate: formData.purchaseDate,
        reference: formData.reference.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        taxRate: formData.taxRate,
        items: lineItems.map((li) => ({
          productId: li.productId,
          quantity: parseInt(li.quantity),
          unitCost: parseFloat(li.unitCost),
        })),
      };

      const res = await api.post('/api/purchases/invoices', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases', 'invoices'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setIsCreateModalOpen(false);
      toast.success('Purchase invoice created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create purchase invoice');
    },
  });

  const addLineItem = () => {
    setLineItems([...lineItems, { productId: '', quantity: '1', unitCost: '0' }]);
  };

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const computedTotals = useMemo(() => {
    let subtotal = 0;
    for (const li of lineItems) {
      const qty = parseInt(li.quantity || '0');
      const unit = parseFloat(li.unitCost || '0');
      subtotal += qty * unit;
    }
    const tax = subtotal * (parseFloat(formData.taxRate || '0') / 100);
    const total = subtotal + tax;
    return { subtotal, tax, total };
  }, [lineItems, formData.taxRate]);

  const canSave = useMemo(() => {
    if (!lineItems.length) return false;
    for (const li of lineItems) {
      if (!li.productId) return false;
      if (!li.quantity || parseInt(li.quantity) < 1) return false;
      if (li.unitCost === '' || Number.isNaN(parseFloat(li.unitCost))) return false;
    }
    return true;
  }, [lineItems]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchases</h1>
          <p className="mt-1 text-sm text-gray-600">Record supplier purchases and receive stock</p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="h-4 w-4 mr-2" />
          New Purchase
        </Button>
      </div>

      <div className="card">
        <div className="card-body">
          <Input
            placeholder="Search purchase #, supplier, reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search className="h-4 w-4 text-gray-400" />}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Purchase Invoices</h2>
          <div className="text-sm text-gray-600">Total: {filteredInvoices.length}</div>
        </div>
        <div className="card-body">
          {invoicesLoading ? (
            <div className="text-sm text-gray-600">Loading purchases...</div>
          ) : invoicesError ? (
            <div className="text-sm text-red-600">Failed to load purchases</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purchase #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-6 text-sm text-gray-500 text-center">
                        No purchases found
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map((inv) => (
                      <tr key={inv.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{inv.purchaseNumber}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(inv.purchaseDate)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{inv.supplierName || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{inv.status}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatCurrency(Number(inv.totalAmount) || 0)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                          <div className="inline-flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewInvoiceMutation.mutate(inv.id)}
                              loading={viewInvoiceMutation.isPending}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteMutation.mutate(inv.id)}
                              loading={deleteMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">New Purchase</h3>
              <button onClick={closeCreateModal} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Supplier (name)"
                  value={formData.supplierName}
                  onChange={(e) => setFormData({ ...formData, supplierName: e.target.value })}
                />
                <Input
                  label="Purchase Date"
                  type="date"
                  value={formData.purchaseDate}
                  onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                />
                <Input
                  label="Reference"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                />
                <Input
                  label="Tax Rate (%)"
                  value={formData.taxRate}
                  onChange={(e) => setFormData({ ...formData, taxRate: e.target.value })}
                />
              </div>

              <Input
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />

              <div className="border rounded-lg">
                <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center">
                    <Package className="h-5 w-5 mr-2 text-gray-600" />
                    <span className="text-sm font-medium text-gray-900">Items</span>
                  </div>
                  <Button variant="secondary" size="sm" onClick={addLineItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>

                <div className="p-4 space-y-3">
                  {lineItems.map((li, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                      <div className="md:col-span-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                        <select
                          className="input"
                          value={li.productId}
                          onChange={(e) => {
                            const updated = [...lineItems];
                            updated[idx] = { ...updated[idx], productId: e.target.value };
                            const product = products.find((p) => p.id === e.target.value);
                            if (product && (updated[idx].unitCost === '0' || updated[idx].unitCost === '')) {
                              updated[idx].unitCost = String(product.cost ?? 0);
                            }
                            setLineItems(updated);
                          }}
                        >
                          <option value="">Select product</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} (Stock: {p.stockQuantity})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <Input
                          label="Qty"
                          value={li.quantity}
                          onChange={(e) => {
                            const updated = [...lineItems];
                            updated[idx] = { ...updated[idx], quantity: e.target.value };
                            setLineItems(updated);
                          }}
                        />
                      </div>

                      <div className="md:col-span-3">
                        <Input
                          label="Unit Cost"
                          value={li.unitCost}
                          onChange={(e) => {
                            const updated = [...lineItems];
                            updated[idx] = { ...updated[idx], unitCost: e.target.value };
                            setLineItems(updated);
                          }}
                        />
                      </div>

                      <div className="md:col-span-1 flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLineItem(idx)}
                          disabled={lineItems.length === 1}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  <div className="pt-3 border-t grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="text-gray-700">Subtotal: <span className="font-medium text-gray-900">{formatCurrency(computedTotals.subtotal)}</span></div>
                    <div className="text-gray-700">Tax: <span className="font-medium text-gray-900">{formatCurrency(computedTotals.tax)}</span></div>
                    <div className="text-gray-700">Total: <span className="font-medium text-gray-900">{formatCurrency(computedTotals.total)}</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={closeCreateModal}>Cancel</Button>
              <Button onClick={() => createMutation.mutate()} loading={createMutation.isPending} disabled={!canSave}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {isViewModalOpen && viewingInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Purchase #{viewingInvoice.purchaseNumber}</h3>
              <button
                onClick={() => {
                  setIsViewModalOpen(false);
                  setViewingInvoice(null);
                  setViewingLineItems([]);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-gray-600">Supplier</div>
                  <div className="font-medium text-gray-900">{viewingInvoice.supplierName || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-600">Purchase Date</div>
                  <div className="font-medium text-gray-900">{formatDate(viewingInvoice.purchaseDate)}</div>
                </div>
                <div>
                  <div className="text-gray-600">Status</div>
                  <div className="font-medium text-gray-900">{viewingInvoice.status}</div>
                </div>
              </div>

              <div className="border rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Cost</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {viewingLineItems.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-6 text-sm text-gray-500 text-center">No line items</td>
                      </tr>
                    ) : (
                      viewingLineItems.map((li) => {
                        const product = products.find((p) => p.id === li.productId);
                        return (
                          <tr key={li.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product?.name || li.productId}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{li.quantity}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatCurrency(Number(li.unitCost) || 0)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatCurrency(Number(li.totalCost) || 0)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="text-gray-700">Subtotal: <span className="font-medium text-gray-900">{formatCurrency(Number(viewingInvoice.subtotal) || 0)}</span></div>
                <div className="text-gray-700">Tax: <span className="font-medium text-gray-900">{formatCurrency(Number(viewingInvoice.taxAmount) || 0)}</span></div>
                <div className="text-gray-700">Total: <span className="font-medium text-gray-900">{formatCurrency(Number(viewingInvoice.totalAmount) || 0)}</span></div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end">
              <Button
                variant="ghost"
                onClick={() => {
                  setIsViewModalOpen(false);
                  setViewingInvoice(null);
                  setViewingLineItems([]);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchasesPage;
